import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { BookOpen, Check, X } from 'lucide-react';
import { CoverImage } from '@/components/CoverImage';
import { Link } from 'react-router-dom';

interface Book {
  id: string;
  title: string;
  authors: string[];
  cover_url?: string;
  cover_cache_url?: string;
  current_page?: number;
  total_pages?: number;
  reading_progress_percent?: number;
  started_at?: string;
}

interface CurrentlyReadingShowcaseProps {
  book: Book;
  isOwnProfile: boolean;
  onUpdateProgress?: () => void;
  onMarkAsRead?: () => void;
  onMarkAsUnfinished?: () => void;
}

export function CurrentlyReadingShowcase({
  book,
  isOwnProfile,
  onUpdateProgress,
  onMarkAsRead,
  onMarkAsUnfinished,
}: CurrentlyReadingShowcaseProps) {
  const progress = book.reading_progress_percent || 0;
  const startedDate = book.started_at ? new Date(book.started_at).toLocaleDateString() : null;

  return (
    <Card className="overflow-hidden border-2 border-primary/20 bg-gradient-to-br from-primary/5 via-background to-background">
      <CardContent className="p-6">
        <div className="flex items-start gap-6">
          {/* Book Cover */}
          <Link to={`/book/${book.id}`} className="shrink-0 group">
            <div className="relative w-32 h-48 rounded-lg overflow-hidden shadow-lg ring-2 ring-primary/20 transition-all duration-300 group-hover:ring-primary/40 group-hover:shadow-xl">
              <CoverImage
                coverUrl={book.cover_cache_url || book.cover_url}
                title={book.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end justify-center pb-2">
                <span className="text-white text-xs font-medium">View Details</span>
              </div>
            </div>
          </Link>

          {/* Book Info & Progress */}
          <div className="flex-1 min-w-0 space-y-4">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <BookOpen className="h-5 w-5 text-primary" />
                <span className="text-sm font-medium text-muted-foreground">Currently Reading</span>
              </div>
              <Link to={`/book/${book.id}`} className="group">
                <h3 className="text-2xl font-bold text-foreground group-hover:text-primary transition-colors line-clamp-2">
                  {book.title}
                </h3>
              </Link>
              <p className="text-sm text-muted-foreground mt-1">
                by {book.authors.join(', ')}
              </p>
              {startedDate && (
                <p className="text-xs text-muted-foreground mt-1">
                  Started {startedDate}
                </p>
              )}
            </div>

            {/* Progress Section */}
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="font-medium text-foreground">Reading Progress</span>
                <span className="text-muted-foreground">
                  {book.current_page || 0} / {book.total_pages || '?'} pages
                </span>
              </div>
              <Progress value={progress} className="h-3" />
              <div className="flex items-center justify-between">
                <span className="text-lg font-bold text-primary">{Math.round(progress)}%</span>
                {progress > 0 && progress < 100 && (
                  <span className="text-xs text-muted-foreground">
                    {progress < 25 ? 'Just getting started' : 
                     progress < 50 ? 'Making good progress' : 
                     progress < 75 ? 'Over halfway there!' : 
                     'Almost finished!'}
                  </span>
                )}
              </div>
            </div>

            {/* Quick Actions */}
            {isOwnProfile && (
              <div className="flex flex-wrap gap-2 pt-2">
                <Button 
                  onClick={onUpdateProgress}
                  size="sm"
                  variant="default"
                  className="gap-2"
                >
                  <BookOpen className="h-4 w-4" />
                  Update Progress
                </Button>
                <Button 
                  onClick={onMarkAsRead}
                  size="sm"
                  variant="outline"
                  className="gap-2"
                >
                  <Check className="h-4 w-4" />
                  Mark as Read
                </Button>
                <Button 
                  onClick={onMarkAsUnfinished}
                  size="sm"
                  variant="ghost"
                  className="gap-2"
                >
                  <X className="h-4 w-4" />
                  Mark as Unfinished
                </Button>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
